import React from "react";
import Layout from "@/components/Layout";
import PageMeta from "@/components/seo/PageMeta";
import { Button } from "@/components/ui/button";
import { Calendar, Mail } from "lucide-react";
import { BOOKING_URL, CONTACT_EMAIL } from "@/config/constants";

const AboutPage: React.FC = () => {
  return (
    <Layout>
      <PageMeta
        title="About | BoardSight by Techné AI"
        description="BoardSight is an initiative of Techné AI, helping Audit and Risk Committees build defensible AI oversight with practical governance tools."
        canonicalUrl="https://boardsight.ai/about"
      />

      {/* Hero */}
      <section className="pt-16 pb-20 md:pt-24 md:pb-28 bg-gradient-to-b from-muted/50 to-background">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground leading-tight mb-6">
              About BoardSight
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground">
              BoardSight is an initiative of Techné AI—purpose-built to help Audit and Risk Committees navigate AI oversight with confidence.
            </p>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto space-y-8">
            <div>
              <h2 className="text-3xl font-bold text-foreground mb-4">Our Mission</h2>
              <p className="text-lg text-muted-foreground">
                AI is transforming how organizations operate—but governance hasn't kept pace. Boards are increasingly expected to demonstrate clear, defensible oversight of AI risk, yet most committees lack the frameworks, checklists, and reporting structures to do so effectively.
              </p>
            </div>
            <div>
              <p className="text-lg text-muted-foreground">
                BoardSight exists to close that gap. We equip Audit and Risk Committees with the practical tools they need—committee-ready slide decks, chair checklists, 30/60/90-day action plans, and ongoing advisory support—so oversight becomes practical, not theoretical.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto space-y-8">
            <h2 className="text-3xl font-bold text-foreground mb-4">Our Approach</h2>
            <p className="text-lg text-muted-foreground">
              We don't deliver generic AI training. Every engagement is structured around the specific oversight responsibilities of Audit and Risk Committees:
            </p>
            <ul className="space-y-3 text-lg text-muted-foreground list-disc list-inside">
              <li>Governance structure, decision rights, and escalation</li>
              <li>Pre- and post-deployment controls</li>
              <li>Third-party and vendor AI risk</li>
              <li>Internal audit assurance planning</li>
              <li>Reporting cadence and committee-grade dashboards</li>
            </ul>
            <p className="text-lg text-muted-foreground">
              Our work is informed by leading frameworks including NIST AI RMF, ISO 42001, the EU AI Act, and sector-specific regulatory guidance.
            </p>
          </div>
        </div>
      </section>

      {/* Who we serve */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-foreground mb-4">Who We Serve</h2>
            <p className="text-lg text-muted-foreground mb-6">
              BoardSight is designed for Audit Committee Chairs, Risk Committee Chairs, and Directors overseeing enterprise risk management, compliance, cyber/privacy, third-party risk, and internal audit.
            </p>
            <p className="text-lg text-muted-foreground">
              Our clients span public and private companies, non-profits, and government entities—any organization where the board or oversight body bears responsibility for enterprise risk, including AI.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-foreground mb-6">
              Ready to make AI oversight practical?
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Book a Chair Call
                </a>
              </Button>
              <Button asChild variant="outline" size="lg">
                <a href={`mailto:${CONTACT_EMAIL}`} className="inline-flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  {CONTACT_EMAIL}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default AboutPage;
